import { Controller } from '@hotwired/stimulus'
import { get } from '@rails/request.js'
import _ from 'lodash'

export default class extends Controller {
  static targets = [
    'input',
    'results',
    'selectedId',
    'courtCity',
    'courtState',
    'courtStreet',
    'courtZip',
    'submit',
    'error',
    'spinner',
  ]
  static values = { state: String }

  initialize() {
    this.searchDebounced = _.debounce(this.performSearch.bind(this), 300)
  }

  connect() {
    this.clickOutsideHandler = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.clickOutsideHandler)

    this.keyboardHandler = this.handleKeyboard.bind(this)
    this.inputTarget.addEventListener('keydown', this.keyboardHandler)
  }

  disconnect() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
    }
    if (this.keyboardHandler) {
      this.inputTarget.removeEventListener('keydown', this.keyboardHandler)
    }
    if (this.searchDebounced) {
      this.searchDebounced.cancel()
    }
  }

  search(event) {
    if (!event.target) return

    const query = event.target.value.trim()
    this.selectedIdTarget.value = ''
    this.clearError()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.showSpinner()
    this.searchDebounced(query)
  }

  async performSearch(query) {
    if (!query) return

    try {
      const state = this.stateValue || ''
      const url = `/api/courts/search?query=${encodeURIComponent(query)}&state=${encodeURIComponent(state)}`

      const response = await get(url, {
        responseKind: 'json',
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json
      this.renderResults(data.results)
    } catch {
      this.showError('Failed to search courts. Please try again.')
      this.hideResults()
    } finally {
      this.hideSpinner()
    }
  }

  renderResults(results) {
    if (!results || !Array.isArray(results) || results.length === 0) {
      this.showNoResults()
      return
    }

    try {
      this.resultsTarget.innerHTML = results
        .map((result, index) => {
          console.log('Processing result:', result)
          return `
                <div class="search-result p-3 cursor-pointer" 
                     data-action="click->court-search#selectResult keydown->court-search#handleResultKeydown"
                     data-id="${this.escapeHtml(result.id)}"
                     data-name="${this.escapeHtml(result.name)}"
                     data-caption-name="${this.escapeHtml(result.caption_name || '')}"
                     data-mailing-name="${this.escapeHtml(result.mailing_name || '')}"
                     data-mailing-city="${this.escapeHtml(result.mailing_city || '')}"
                     data-mailing-state="${this.escapeHtml(result.mailing_state || '')}"
                     data-mailing-street="${this.escapeHtml(result.mailing_street || '')}"
                     data-mailing-zip="${this.escapeHtml(result.mailing_zip || '')}"
                     data-index="${index}"
                     tabindex="0"
                     role="option">
                    <div class="fw-medium">${this.escapeHtml(result.name)}</div>
                    <div class="text-muted small">${this.escapeHtml(result.formatted_address)}</div>
                </div>
            `
        })
        .join('')
      this.showResults()
    } catch {
      this.showError('Failed to display results. Please try again.')
      this.hideResults()
    }
  }

  showNoResults() {
    this.resultsTarget.innerHTML = `
            <div class="no-results">
                <div>No courts found</div>
                <small>Try adjusting your search terms</small>
            </div>
        `
    this.showResults()
  }

  selectResult(event) {
    if (!event.currentTarget) return

    try {
      const dataset = event.currentTarget.dataset
      const {
        id,
        captionName,
        mailingName,
        name,
        mailingCity,
        mailingState,
        mailingStreet,
        mailingZip,
      } = dataset

      if (!id) {
        throw new Error('Invalid selection data')
      }

      const displayName = name || captionName || mailingName || 'Unknown Court'
      const formattedName = displayName.replace(/\\n/g, '\n')

      this.inputTarget.value = formattedName
      this.selectedIdTarget.value = id

      if (this.hasCourtCityTarget) this.courtCityTarget.value = mailingCity || ''
      if (this.hasCourtStateTarget) this.courtStateTarget.value = mailingState || ''
      if (this.hasCourtStreetTarget) this.courtStreetTarget.value = mailingStreet || ''
      if (this.hasCourtZipTarget) this.courtZipTarget.value = mailingZip || ''

      this.hideResults()
      this.clearError()

      this.saveCourtSelection(
        formattedName,
        id,
        mailingCity,
        mailingState,
        mailingStreet,
        mailingZip
      )
    } catch {
      this.showError('Failed to select court. Please try again.')
    }
  }

  handleKeyboard(event) {
    const results = this.resultsTarget.querySelectorAll('.search-result')
    if (results.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigateResults(results, 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.navigateResults(results, -1)
        break
      case 'Enter': {
        const focused = this.resultsTarget.querySelector('.search-result:focus')
        event.preventDefault()
        if (focused) {
          this.selectResult({ currentTarget: focused })
        }
        break
      }
      case 'Escape':
        this.hideResults()
        break
    }
  }

  handleResultKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.selectResult(event)
    }
  }

  navigateResults(results, direction) {
    const currentIndex = Array.from(results).findIndex(result => result === document.activeElement)
    let newIndex = currentIndex + direction

    if (newIndex < 0) newIndex = results.length - 1
    if (newIndex >= results.length) newIndex = 0

    results[newIndex].focus()
  }

  async saveCourtSelection(
    courtName,
    selectedCourtId,
    courtCity,
    courtState,
    courtStreet,
    courtZipCode
  ) {
    try {
      const customerDocumentId = this.getCustomerDocumentId()
      if (!customerDocumentId) {
        throw new Error('Customer document ID not found')
      }

      const response = await fetch(`/api_controller/save_court/${customerDocumentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({
          customer_document_id: customerDocumentId,
          court_name: courtName,
          selected_court_id: selectedCourtId,
          court_city: courtCity,
          court_state: courtState,
          court_street: courtStreet,
          court_zip_code: courtZipCode,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save court selection')
      }
    } catch {
      this.showError('Failed to save court selection. Please try again.')
    }
  }

  getCustomerDocumentId() {
    const pathParts = window.location.pathname.split('/')

    const documentTypes = ['debt_answers', 'solosettles']
    for (const docType of documentTypes) {
      const docIndex = pathParts.indexOf(docType)
      if (docIndex !== -1 && pathParts[docIndex + 1]) {
        const id = pathParts[docIndex + 1]
        return id
      }
    }

    console.log('Customer document ID not found')
    return null
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  showResults() {
    this.resultsTarget.classList.add('show')
    requestAnimationFrame(() => {
      this.resultsTarget.style.display = 'block'
    })
  }

  hideResults() {
    this.resultsTarget.classList.remove('show')
    setTimeout(() => {
      if (!this.resultsTarget.classList.contains('show')) {
        this.resultsTarget.style.display = 'none'
      }
    }, 300)
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove('d-none')
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('d-none')
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.style.display = 'block'
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ''
      this.errorTarget.style.display = 'none'
    }
  }

  escapeHtml(unsafe) {
    if (unsafe === null || unsafe === undefined) return ''
    if (typeof unsafe === 'number') return String(unsafe)
    if (typeof unsafe !== 'string') return String(unsafe || '')

    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }
}
